import { useEffect, useRef, useState, CSSProperties, ReactNode } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { PRESALE_VISIBLE } from '@/config/stripe';
import FoundingMembersBar from '@/components/FoundingMembersBar';
import WaitlistForm from '@/components/WaitlistForm';
import { useAuth } from '@/contexts/SupabaseAuthContext';

// ===== Brand tokens =====
const NAVY = 'var(--navy)';
const CREAM = 'var(--cream)';
const GOLD = 'var(--gold)';
const GOLD_HOVER = 'var(--gold-hover)';
const DARK = 'var(--charcoal)';
const MUTED = 'color-mix(in srgb, var(--navy) 60%, transparent)';

const SERIF = 'var(--serif)';
const SANS = 'var(--sans)';

// Hull section background. Leave empty for the cream/editorial treatment.
// Uses the canon garden-room render (same image as the /hull hotspot stage).
const HULL_BG = '/hull-garden-room.webp';

// ===== Reveal: fade-in on scroll =====
type RevealProps = {
  children: ReactNode;
  delay?: number;
  style?: CSSProperties;
  id?: string;
};

const Reveal: React.FC<RevealProps> = ({ children, delay = 0, style, id }) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const baseStyle: CSSProperties = {
    opacity: visible ? 1 : 0,
    transform: visible ? 'translateY(0)' : 'translateY(32px)',
    transition: `opacity 0.8s ease ${delay}s, transform 0.8s ease ${delay}s`,
    willChange: 'opacity, transform',
    ...style,
  };

  return (
    <div ref={ref} id={id} style={baseStyle}>
      {children}
    </div>
  );
};

// ===== Gold divider =====
const Divider: React.FC = () => (
  <div style={{ padding: '48px 0', display: 'flex', justifyContent: 'center' }}>
    <div style={{ width: 60, height: 1, background: GOLD }} />
  </div>
);

// ===== Render image with cream gradient overlay =====
type RenderImageProps = {
  src: string;
  alt: string;
  caption?: string;
  marginTop?: number;
};

const RenderImage: React.FC<RenderImageProps> = ({ src, alt, caption, marginTop = 0 }) => (
  <Reveal style={{ marginTop }}>
    <div style={{ position: 'relative', maxWidth: 900, margin: '0 auto', width: '100%' }}>
      <img
        src={src}
        alt={alt}
        style={{
          display: 'block',
          width: '100%',
          maxHeight: 500,
          objectFit: 'cover',
          objectPosition: 'center 60%',
        }}
      />
    </div>
    {caption && (
      <p
        style={{
          fontFamily: SERIF,
          fontSize: 13,
          fontStyle: 'italic',
          color: MUTED,
          opacity: 0.7,
          textAlign: 'center',
          marginTop: 12,
        }}
      >
        {caption}
      </p>
    )}
  </Reveal>
);

// ===== Feature item =====
type FeatureItemProps = { title: string; desc: string };
const FeatureItem: React.FC<FeatureItemProps> = ({ title, desc }) => (
  <div>
    <div style={{ fontFamily: SERIF, fontSize: 18, fontWeight: 500, color: DARK, marginBottom: 6 }}>{title}</div>
    <div style={{ fontFamily: SANS, fontSize: 14, color: MUTED, lineHeight: 1.5 }}>{desc}</div>
  </div>
);

// ===== Page =====
const HomePage: React.FC = () => {
  // Founding-member count, seeded by the members bar's mount fetch and
  // updated to the latest waitlist total after a signup. The signup form
  // feeds the latest count up via its onSuccess callback.
  const [foundingCount, setFoundingCount] = useState<number | undefined>(undefined);
  const { user } = useAuth();

  const sectionPad: CSSProperties = {
    maxWidth: 720,
    margin: '0 auto',
    padding: '0 24px',
    textAlign: 'center',
  };

  return (
    <>
      <Helmet>
        <title>Maslow NYC — The restroom New York deserves.</title>
        <link rel="canonical" href="https://maslow.nyc/" />
        <meta
          name="description"
          content="Maslow is twelve private restrooms in SoHo, New York — a locked door, warm light, and ten minutes that belong to you. Opening 2028. Join the waitlist."
        />
      </Helmet>

      <div style={{ background: CREAM, color: DARK, fontFamily: SANS, minHeight: '100vh' }}>
        {/* ===== NAV ===== */}
        <nav
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '24px 32px',
            maxWidth: 1200,
            margin: '0 auto',
          }}
        >
          <div
            style={{
              fontFamily: SERIF,
              color: NAVY,
              fontSize: 22,
              fontWeight: 600,
              letterSpacing: '0.12em',
            }}
          >
            MASLOW
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 28 }}>
            {/* Quiet member-access affordance — muted ink, never competes with the waitlist CTA. */}
            <Link
              to={user ? '/profile' : '/login'}
              style={{
                fontFamily: SANS,
                fontSize: 13,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                color: MUTED,
                textDecoration: 'none',
              }}
            >
              {user ? 'Profile' : 'Sign in'}
            </Link>
            <a
              href="#signup"
              style={{
                fontFamily: SANS,
                fontSize: 13,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                color: DARK,
                textDecoration: 'none',
              }}
            >
              Join the waitlist
            </a>
          </div>
        </nav>

        {/* ===== HERO ===== */}
        <section
          style={{
            position: 'relative',
            overflow: 'hidden',
            minHeight: '78svh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            padding: '80px 24px 40px',
            textAlign: 'center',
          }}
        >
          <img
            src="/room-skylight-wide.webp"
            alt=""
            width={1800}
            height={1200}
            decoding="async"
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'center',
            }}
          />
          {/* soft dark scrim so light text stays legible over the render */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'color-mix(in srgb, var(--charcoal) 52%, transparent)',
              pointerEvents: 'none',
            }}
          />
          <span
            style={{
              position: 'absolute',
              right: 16,
              bottom: 12,
              fontFamily: 'var(--sans)',
              fontSize: 11,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'color-mix(in srgb, var(--cream) 70%, transparent)',
              pointerEvents: 'none',
            }}
          >
            Concept render
          </span>
          <div style={{ position: 'relative', zIndex: 1 }}>
          <Reveal>
            <h1
              style={{
                fontFamily: SERIF,
                fontWeight: 300,
                fontSize: 'clamp(36px, 6vw, 64px)',
                lineHeight: 1.15,
                color: 'var(--cream)',
                maxWidth: 900,
                margin: '0 auto 28px',
              }}
            >
              The restroom New York deserves.
            </h1>
          </Reveal>
          <Reveal delay={0.15}>
            <p
              style={{
                fontFamily: SANS,
                fontSize: 'clamp(16px, 2vw, 19px)',
                lineHeight: 1.6,
                color: 'var(--cream)',
                maxWidth: 600,
                margin: '0 auto 40px',
              }}
            >
              Twelve private rooms in SoHo. A locked door, warm light, and ten minutes that belong to you. Opening 2028.
            </p>
          </Reveal>
          <Reveal delay={0.25}>
            <WaitlistForm source="hero" showPosition onSuccess={setFoundingCount} onDark />
          </Reveal>
          </div>
          {/* subtle scroll cue — pure hint, not a control */}
          <div
            aria-hidden="true"
            className="animate-pulse"
            style={{
              position: 'absolute',
              bottom: 20,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 1,
              height: 48,
              background: 'color-mix(in srgb, var(--cream) 60%, transparent)',
              pointerEvents: 'none',
            }}
          />
        </section>

        {/* ===== FOUNDING MEMBERS (Phase 0) ===== */}
        <section id="founding" style={{ ...sectionPad, maxWidth: 720, paddingTop: 24 }}>
          <Reveal>
            {/* Signup lives in the hero and the #signup section below; the bar
                reflects those joins live via the shared foundingCount state. */}
            <FoundingMembersBar count={foundingCount} />
          </Reveal>
        </section>

        <Divider />

        <div style={{ padding: '48px 24px 0' }}>
          <RenderImage
            src="/room-skylight.webp"
            alt="Maslow private room — concept render"
            caption="Concept render — final fixtures and finishes in development"
          />
          <div style={{ textAlign: 'center' }}>
            <Link
              to="/rooms"
              style={{
                fontFamily: SANS,
                fontSize: 13,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: DARK,
                textDecoration: 'none',
                borderBottom: '1px solid color-mix(in srgb, var(--gold) 40%, transparent)',
                paddingBottom: 2,
                display: 'inline-block',
                marginTop: 24,
              }}
            >
              See the rooms →
            </Link>
          </div>
        </div>

        <Divider />

        {/* ===== PRESALE (teaser) ===== */}
        {PRESALE_VISIBLE && (
        <section id="presale" style={sectionPad}>
          <Reveal>
            <h2
              style={{
                fontFamily: SERIF,
                fontWeight: 300,
                fontSize: 'clamp(28px, 4vw, 40px)',
                color: DARK,
                marginBottom: 20,
                lineHeight: 1.2,
              }}
            >
              Before the doors open.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p style={{ fontFamily: SANS, fontSize: 16, color: MUTED, lineHeight: 1.6, maxWidth: 560, margin: '0 auto', textAlign: 'center' }}>
              Passes and bricks for the SoHo flagship — first access goes to the list.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <div style={{ textAlign: 'center' }}>
              <Link
                to="/build"
                style={{
                  fontFamily: SANS,
                  fontSize: 13,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: DARK,
                  textDecoration: 'none',
                  borderBottom: '1px solid color-mix(in srgb, var(--gold) 40%, transparent)',
                  paddingBottom: 2,
                  display: 'inline-block',
                  marginTop: 24,
                }}
              >
                See what's coming →
              </Link>
            </div>
          </Reveal>
        </section>
        )}

        {/* ===== THE HULL ===== */}
        <section
          style={{
            position: 'relative',
            overflow: 'hidden',
            minHeight: '70svh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            padding: '64px 24px',
            textAlign: 'center',
            background: DARK,
            color: CREAM,
          }}
        >
          {HULL_BG && (
            <>
              <img
                src={HULL_BG}
                alt=""
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: 'center',
                }}
              />
              {/* soft dark scrim so light text stays legible over the render */}
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'color-mix(in srgb, var(--charcoal) 55%, transparent)',
                  pointerEvents: 'none',
                }}
              />
              <span
                style={{
                  position: 'absolute',
                  right: 16,
                  bottom: 12,
                  fontFamily: 'var(--sans)',
                  fontSize: 11,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: 'color-mix(in srgb, var(--cream) 70%, transparent)',
                  pointerEvents: 'none',
                }}
              >
                Concept render
              </span>
            </>
          )}
          <Reveal>
            <div style={{ position: 'relative', zIndex: 1, maxWidth: 820, margin: '0 auto' }}>
              <h2
                style={{
                  fontFamily: SERIF,
                  fontWeight: 300,
                  fontSize: 'clamp(28px, 4vw, 40px)',
                  marginBottom: 20,
                  lineHeight: 1.2,
                }}
              >
                The Hull
              </h2>
              <p
                style={{
                  fontFamily: SERIF,
                  fontStyle: 'italic',
                  fontWeight: 300,
                  fontSize: 'clamp(20px, 2.6vw, 28px)',
                  color: CREAM,
                  opacity: 0.95,
                  margin: '8px 0 0',
                }}
              >
                Where the city can wait.
              </p>
              {/* thin gold accent rule, matching the dividers elsewhere */}
              <div style={{ width: 60, height: 1, background: GOLD, margin: '0 auto 28px' }} />
              <p
                style={{
                  fontFamily: SANS,
                  fontSize: 16,
                  lineHeight: 1.7,
                  marginBottom: 24,
                  color: HULL_BG ? CREAM : DARK,
                }}
              >
                A free communal space in the center of it all. Water, ice, phone charging, a living wall with
                water streaming down its face, and a seat. No purchase required. No questions asked. Just walk in.
              </p>
              <p
                style={{
                  fontFamily: SERIF,
                  fontStyle: 'italic',
                  fontSize: 17,
                  lineHeight: 1.5,
                  color: HULL_BG ? CREAM : MUTED,
                  opacity: HULL_BG ? 0.9 : 1,
                }}
              >
                The people who pay for rooms cover free access for everyone else. That's the whole model.
              </p>
              <Link
                to="/hull"
                style={{
                  fontFamily: SANS,
                  fontSize: 13,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: CREAM,
                  textDecoration: 'none',
                  borderBottom: '1px solid color-mix(in srgb, var(--cream) 40%, transparent)',
                  paddingBottom: 2,
                  display: 'inline-block',
                  marginTop: 28,
                }}
              >
                Step inside the Hull →
              </Link>
            </div>
          </Reveal>
        </section>

        <Divider />

        {/* ===== EMAIL CAPTURE ===== */}
        <section id="signup" style={sectionPad}>
          <Reveal>
            <h2
              style={{
                fontFamily: SERIF,
                fontWeight: 300,
                fontSize: 'clamp(28px, 4vw, 40px)',
                color: DARK,
                marginBottom: 20,
                lineHeight: 1.2,
              }}
            >
              Be the first to know.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p style={{ fontFamily: SANS, fontSize: 16, color: MUTED, marginBottom: 36, lineHeight: 1.6 }}>
              I'm building something SoHo has never had. Leave your email — you'll hear it here first.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <WaitlistForm source="footer" showPosition onSuccess={setFoundingCount} />
          </Reveal>
        </section>

        {/* ===== FOOTER ===== */}
        <footer style={{ padding: '80px 24px 48px', textAlign: 'center' }}>
          <div
            style={{
              fontFamily: SERIF,
              fontSize: 13,
              letterSpacing: '0.1em',
              color: MUTED,
              marginBottom: 12,
            }}
          >
            MASLOW LLC
          </div>
          <div
            style={{
              fontFamily: SERIF,
              fontSize: 14,
              fontStyle: 'italic',
              color: MUTED,
              opacity: 0.7,
            }}
          >
            Premium is the floor, not an upgrade.
          </div>
          <button
            onClick={() => (window as any).UC_UI?.showSecondLayer?.()}
            onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
            onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
            style={{
              marginTop: 24,
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              fontFamily: SANS,
              fontSize: 13,
              color: MUTED,
              textDecoration: 'none',
            }}
          >
            Cookie Settings
          </button>
        </footer>
      </div>
    </>
  );
};

export default HomePage;
